import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "contexts/AuthContext";
import { updateUserDoc } from "../../../../../firebase";
import { RootState } from "redux/reducers/index";
import { setErrors, clearErrors } from "redux/actions/errors";

import StandardButton from "../../../../Common/buttons/StandardButton";
import { generatePageTitle } from "utils/general.utils";

const EditAccountInformation: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { errors } = useSelector((state: RootState) => state);
  const [name, setName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    document.title = generatePageTitle("Edit Account Information");
  }, []);

  useEffect(() => {
    setName(currentUser.name);
    setTitle(currentUser.title);
    setPhone(currentUser.phone);

    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch, currentUser.name, currentUser.title, currentUser.phone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearErrors());

    // dispatch changes to server
    const update = {
      name,
      title,
      phone,
    };

    try {
      setLoading(true);
      await updateUserDoc(update, currentUser);
      navigate("/dashboard/account-info");
    } catch {
      dispatch(setErrors({ message: "Failed to update profile " }));
    }

    setLoading(false);
  };

  return (
    <EditForm onSubmit={handleSubmit}>
      <h2>
        <strong>Edit Profile</strong>
      </h2>
      <div>
        <Label htmlFor="name">Name</Label>
        <Input
          type="text"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="title">Professional Title</Label>
        <Input
          type="text"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone Number</Label>
        <Input
          type="tel"
          name="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      <Error>{errors.message}</Error>
      <Buttons>
        <StandardButton type="submit" size="sm" disabled={loading}>
          Save
        </StandardButton>
        <Link to="/dashboard/account-info">
          <StandardButton
            type="button"
            size="sm"
            color="red"
            disabled={loading}
          >
            Cancel
          </StandardButton>
        </Link>
      </Buttons>
    </EditForm>
  );
};

const EditForm = styled.form`
  background-color: #fff;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.3);
  padding: 2rem;
  margin: 10rem auto;
  width: 100%;
`;

const Label = styled.label`
  text-align: right;
  width: 10rem;
`;

const Input = styled.input`
  border-bottom: 2px solid ${(props) => props.theme.colors.lighterGrey};
  border-radius: 0;
  font-size: 2rem;
  padding: 0.5rem;
  margin-bottom: 1rem;
  margin-left: 1rem;
  height: 4rem;
  width: 50rem;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;

  button {
    margin-right: 2rem;
  }
`;

const Error = styled.p`
  color: ${(props) => props.theme.colors.red};
  font-size: 1.4rem;
  font-weight: 700rem;
`;

export default EditAccountInformation;
